import { Children, useMemo, type ReactNode, useRef } from 'react';
import {
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormLabel,
  Box,
} from '@chakra-ui/react';
import {
  type FieldPathByValue,
  type FieldValues,
  type RegisterOptions,
  useFormContext,
  Controller,
  get,
} from 'react-hook-form';
import {
  Select as ChakraReactSelect,
  components as chakraSelectComponents,
  type ChakraStylesConfig,
  type GroupBase,
  type MenuListProps,
  type Props as ChakraReactSelectProps,
} from 'chakra-react-select';
import { List, type RowComponentProps } from 'react-window';

export type FormSearchSelectOption<OptValueType> = {
  value: OptValueType;
  label: string;
};

type ChakraSelectProps<Option> = Partial<
  ChakraReactSelectProps<Option, false, GroupBase<Option>>
>;

type VirtualizedMenuRowProps = {
  items: ReactNode[];
};

type VirtualizedMenuRowComponentProps =
  RowComponentProps<VirtualizedMenuRowProps>;

const VirtualizedMenuRow = ({
  ariaAttributes,
  index,
  style,
  items,
}: VirtualizedMenuRowComponentProps) => (
  <Box
    {...ariaAttributes}
    style={{
      ...style,
      width: '100%',
    }}>
    {items[index] ?? null}
  </Box>
);

const MENU_ITEM_HEIGHT = 36;
const DEFAULT_VISIBLE_ITEM_COUNT = 6;
function VirtualizedMenuList<
  OptValueType extends string | { value: string; label: string },
>(props: MenuListProps<FormSearchSelectOption<OptValueType>, false>) {
  const { children, maxHeight } = props;
  const menuListRef = useRef<HTMLDivElement>(null);
  const items = useMemo(() => Children.toArray(children), [children]);

  const itemCount = items.length;

  if (itemCount === 0) {
    return (
      <chakraSelectComponents.MenuList {...props}>
        {children}
      </chakraSelectComponents.MenuList>
    );
  }

  const height = Math.min(
    maxHeight ?? MENU_ITEM_HEIGHT * DEFAULT_VISIBLE_ITEM_COUNT,
    itemCount * MENU_ITEM_HEIGHT,
  );

  return (
    <chakraSelectComponents.MenuList
      {...props}
      innerRef={menuListRef}
      innerProps={{ style: { overflow: 'hidden' } }}>
      <List
        style={{ height, width: '100%' }}
        defaultHeight={height}
        rowCount={itemCount}
        rowHeight={MENU_ITEM_HEIGHT}
        rowComponent={VirtualizedMenuRow}
        rowProps={{ items }}
        overscanCount={5}
      />
    </chakraSelectComponents.MenuList>
  );
}

type FormSearchSelectProps<
  FormInputs extends FieldValues,
  OptValueType extends string | { value: string; label: string },
> = {
  inputPath: FieldPathByValue<FormInputs, OptValueType>;
  options: Array<FormSearchSelectOption<OptValueType>>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  selectProps?: ChakraSelectProps<FormSearchSelectOption<OptValueType>>;
} & FormControlProps;

function createDefaultChakraStyles<
  OptValueType extends string | { value: string; label: string },
>(): ChakraStylesConfig<FormSearchSelectOption<OptValueType>, false> {
  return {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'surface.hover',
      borderColor: state.isFocused ? 'blue.500' : 'transparent',
      boxShadow: state.isFocused
        ? '0 0 0 1px var(--chakra-colors-blue-500)'
        : 'none',
      _hover: {
        borderColor: 'border.box',
      },
      borderRadius: 'md',
      minHeight: '40px',
      fontFamily: 'marketingPage',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 10,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'gray.400',
      fontFamily: 'marketingPage',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? 'surface.hover' : 'surface.overlay',
      color: 'text.body',
      fontFamily: 'marketingPage',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'text.body',
      fontFamily: 'marketingPage',
    }),
  };
}

export function FormSearchSelect<
  FormInputs extends FieldValues,
  OptValueType extends string | { value: string; label: string },
>({
  inputPath,
  options,
  placeholder,
  label,
  required,
  selectProps,
  ...formControlProps
}: FormSearchSelectProps<FormInputs, OptValueType>) {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormInputs>();

  const rules: RegisterOptions<
    FormInputs,
    FieldPathByValue<FormInputs, OptValueType>
  > = required ? { required: 'Required' } : {};

  const {
    chakraStyles,
    components: customComponents,
    ...restSelectProps
  } = selectProps ?? {};

  const error = get(errors, inputPath);
  return (
    <FormControl isInvalid={!!error} {...formControlProps}>
      {label && (
        <FormLabel
          htmlFor={inputPath}
          color="text.muted"
          fontWeight="medium"
          mb={2}
          fontSize="sm">
          {label}
        </FormLabel>
      )}
      <Controller
        name={inputPath}
        control={control}
        rules={rules}
        render={({ field }) => {
          // here we hadnle both string values and object values
          const fieldValue =
            typeof field.value === 'string'
              ? field.value
              : (field.value?.value ?? null);
          const selectedOption =
            options.find(({ value }) => value === fieldValue) ?? null;
          return (
            <ChakraReactSelect
              instanceId={inputPath}
              inputId={inputPath}
              options={options}
              placeholder={placeholder}
              value={selectedOption}
              onChange={(option) => {
                field.onChange(
                  typeof field.value === 'string' ? option?.value : option,
                );
              }}
              onBlur={field.onBlur}
              isClearable
              isSearchable
              chakraStyles={
                chakraStyles ?? createDefaultChakraStyles<OptValueType>()
              }
              components={{
                MenuList: VirtualizedMenuList,
                ...(customComponents ?? {}),
              }}
              {...restSelectProps}
            />
          );
        }}
      />
      {error && typeof (error as { message?: string }).message === 'string' && (
        <FormErrorMessage color="red" fontSize="sm">
          {(error as { message?: string }).message}
        </FormErrorMessage>
      )}
    </FormControl>
  );
}
